import { ZigMenu, ZigMenuHandle, ZigSwitchLightDark } from '@zignaly-open/ui';
import { ZigDotsHorizontalIcon } from '@zignaly-open/ui/icons';
import React, { useCallback, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { GlobeLanguagesStyled, LabelButton, NavLink, Networks } from './styles';
import { useTranslation } from 'react-i18next';
import socialNetworksLinks from '../../../util/socialNetworks';
import { supportedLanguages } from '../../../util/i18n/i18next';
import { useChangeLocale, useIsAuthenticated } from '../../../apis/user/use';
import {
  useFirstOwnedService,
  useTraderServices,
} from '../../../apis/service/use';
import { generatePath, useNavigate } from 'react-router-dom';
import {
  ROUTE_BECOME_TRADER,
  ROUTE_TRADING_SERVICE_MANAGE,
} from '../../../routes';
import { LocalizationLanguages } from '../../../util/i18n/languages';
import { HeaderDropdownButton } from '../AccountMenu/styles';
import { ZigMenuProps } from '@zignaly-open/ui/src/components/display/ZigMenu/types';
import { isZignaly, whitelabel } from '../../../whitelabel';
import { ZigMenuOption } from '@zignaly-open/ui/components/display/ZigMenu/types';

const ExtraNavigationDropdown: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dropDownRef = useRef<ZigMenuHandle>(null);
  const { t, i18n } = useTranslation('common');
  const changeLocale = useChangeLocale();
  const service = useFirstOwnedService();
  const { data: traderServices, isFetching } = useTraderServices();
  const isAuthenticated = useIsAuthenticated();
  const isUp1000 = useMediaQuery(theme.breakpoints.up(1000));

  const onClose = useCallback(() => {
    dropDownRef.current?.closeDropDown();
  }, [dropDownRef]);

  const languageMap = supportedLanguages
    ? supportedLanguages.map((x) => LocalizationLanguages[x])
    : Object.values(LocalizationLanguages);

  const handleSelectLanguage = (locale: string) => {
    onSelectLocale(locale);
  };

  const onSelectLocale = (locale: string) => {
    changeLocale(locale);
    onClose();
  };

  const separatorElement = {
    separator: true,
    sx: { marginTop: '11px', marginBottom: '12px', userSelect: 'none' },
  };

  const helperElement =
    !!whitelabel.links?.helpUrl &&
    ({
      label: t('main-menu.dropdown-link-helpDocs'),
      id: 'menu-dropdown__help-docs',
      target: '_blank',
      href: whitelabel.links.helpUrl,
    } as ZigMenuOption);

  const socialLinks = socialNetworksLinks.length > 0 && {
    element: (
      <Networks key={'--social-networks'}>
        {socialNetworksLinks.map((socialNetwork, index) => {
          const IconComponent = socialNetwork.image;
          return (
            <NavLink
              onClick={onClose}
              href={socialNetwork.path}
              key={`--social-network-nav-link-${index.toString()}`}
              id={`menu-dropdown__social-network-${index.toString()}`}
              target={'_blank'}
            >
              <IconComponent height={'22px'} width={'22px'} />
            </NavLink>
          );
        })}
      </Networks>
    ),
    sx: {
      padding: 0,
      marginTop: '12px',
    },
  };

  const languageElement = languageMap.length > 1 && {
    id: 'menu-dropdown__language-switcher',
    label: (
      <>
        <GlobeLanguagesStyled
          // yeah, why not change the icon to a shade slightly different from the text
          color={theme.palette.neutral300}
          width={'26px'}
          height={'26px'}
        />
        <LabelButton variant={'body1'}>
          {LocalizationLanguages[i18n.language?.split('_')[0]]?.label}
        </LabelButton>
      </>
    ),
    sx: !socialLinks ? { marginBottom: '6px' } : undefined,
    children: languageMap.map((language, index) => ({
      id: `menu-dropdown-languages__${index.toString()}`,
      active: i18n.language === language.locale,
      label: language.label,
      onClick: () => handleSelectLanguage(language.locale),
    })),
  };

  const manageService = isAuthenticated &&
    !!traderServices?.length &&
    !isFetching && {
      label: t('main-menu.dropdown-link-forTrading'),
      id: 'menu-dropdown__for-trading',
      href:
        service &&
        generatePath(ROUTE_TRADING_SERVICE_MANAGE, {
          serviceId: service.serviceId?.toString(),
        }),
      onClick: () =>
        navigate(
          service
            ? generatePath(ROUTE_TRADING_SERVICE_MANAGE, {
                serviceId: service.serviceId?.toString(),
              })
            : ROUTE_BECOME_TRADER,
        ),
    };

  const zigSwitchLightDark = isZignaly && {
    element: <ZigSwitchLightDark />,
    id: 'menu-dropdown__switch-theme',
  };

  const becomeTrader = !isUp1000 && {
    label: t('navigation-menu.become-trader'),
    id: 'menu-dropdown__become-trader',
    href: ROUTE_BECOME_TRADER,
    onClick: () => navigate(ROUTE_BECOME_TRADER),
  };

  const options: ZigMenuProps['options'] = [
    manageService,
    becomeTrader,
    helperElement,
    zigSwitchLightDark,
    !!(manageService || becomeTrader || helperElement) &&
      !!languageElement &&
      separatorElement,
    languageElement,
    socialLinks,
  ].filter(Boolean);

  return (
    <ZigMenu
      component={({ open }) => (
        <HeaderDropdownButton id={'menu__dropdown-trading'} active={open}>
          <ZigDotsHorizontalIcon
            width={20}
            height={4}
            color={open ? theme.palette.neutral100 : theme.palette.neutral300}
          />
        </HeaderDropdownButton>
      )}
      options={options}
      menuSx={{ ul: { paddingBottom: 0 } }}
    />
  );
};

export default ExtraNavigationDropdown;
